import { empresaTieneAlgunModulo } from '@/features/auth/tenant-modules';
import { confirmar } from '@/components/ui/confirm-dialog';
import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect, useState } from 'react';
import {
  descargarDocumentoDelEspejo,
  esCodigoDelPricer,
  guardarArchivo,
  mensajeDelServidor,
} from '@/features/pricer-datec/services/documento-pdf';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  Layers,
  Globe,
  AlertTriangle,
  RefreshCw,
  Clock,
  BookmarkPlus,
  Tags,
  FileCheck,
  Copy,
  Download,
  ChevronDown,
  ChevronRight,
  Link2,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useQuoteStore } from '../store/quote-store';
import { formatCurrency, formatDateString, formatNumber } from '@/lib/utils/format';
import { QuoteStatus, VariableType } from '@/types';
import { useToast } from '@/lib/hooks/use-toast';
import { Variable } from '@/features/pricing-models/types/model.types';
import { calculateVariables } from '@/features/pricing-models/utils/formula-evaluator';
import { TagBadge } from '../components/tag-badge';
import { TagSelector } from '../components/tag-selector';
import { CommentsSection } from '../components/comments-section';
import { PanelDeAcceso } from '../components/panel-de-acceso';
import { QuoteLogisticsSection } from '../components/quote-logistics-section';
import { CurrencySimulator } from '../components/currency-simulator';
import { shipmentsService } from '../services/shipments.service';
import { quoteService } from '../services/quote-service';
import type { QuoteShipment } from '../types/quote-shipment.types';
import { ShareClientDialog } from '@/features/public-tracking/components/share-client-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { contractService } from '@/features/contracts/services/contract.service';
import type { Contract } from '@/features/contracts/types/contract.types';
import { FileSignature, Loader2 } from 'lucide-react';

/**
 * Resuelve el precio unitario a mostrar para un line item.
 * Prioridad:
 *   1. lineItem.unitPrice (precio calculado por el modelo, p.ej. variable con priceRole=UNIT_PRICE)
 *   2. costInputs.COSTO_UNITARIO (fallback: costo real introducido cuando el modelo no calcula precio)
 *   3. material.unitPrice (fallback al default del material maestro)
 */
function resolveDisplayUnitPrice(item: any): number {
  const up = Number(item?.unitPrice);
  if (Number.isFinite(up) && up > 0) return up;

  const fromCostInputs = item?.costInputs?.COSTO_UNITARIO;
  const ciNum = fromCostInputs != null && fromCostInputs !== '' ? Number(fromCostInputs) : NaN;
  if (Number.isFinite(ciNum) && ciNum > 0) return ciNum;

  const matPrice = Number(item?.material?.unitPrice ?? item?.material?.price);
  if (Number.isFinite(matPrice) && matPrice > 0) return matPrice;

  return Number(item?.unitPrice) || 0;
}

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    currentQuote,
    isLoading,
    fetchQuote,
    deleteQuote,
    saveAsTemplate,
    removeQuoteTag,
    fetchTags,
  } = useQuoteStore();
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [showTemplatePopover, setShowTemplatePopover] = useState(false);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [shipments, setShipments] = useState<QuoteShipment[]>([]);
  const [quoteContracts, setQuoteContracts] = useState<Contract[]>([]);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [expandedFormulaSections, setExpandedFormulaSections] = useState<Set<string>>(new Set());

  const isImportQuote =
    currentQuote?.quoteType &&
    ['CO', 'IMPORT', 'IMP', 'IMP-SW'].includes(currentQuote.quoteType.code);

  // === Margen Real computado en vivo desde los line items por roles ===
  // Prioridad: variables con priceRole === 'COST_BASE_MARGIN' y 'MARGIN' en el modelo de cada sección.
  // Fallback por nombre de variable para modelos viejos sin roles marcados.
  const liveMargin = (() => {
    const sections = (currentQuote as any)?.sections || [];
    if (sections.length === 0) return null;

    const getVarCodeByRole = (
      vars: Array<{ code: string; priceRole?: string | null }>,
      role: string
    ): string | null => vars.find((v) => v.priceRole === role)?.code ?? null;

    let totalBaseCost = 0;
    let totalMargin = 0;
    let anyMarginFound = false;

    for (const s of sections) {
      const vars = s?.model?.variables || [];
      const baseCode =
        getVarCodeByRole(vars, 'COST_BASE_MARGIN') ??
        getVarCodeByRole(vars, 'COST_INPUT') ??
        getVarCodeByRole(vars, 'UNIT_COST');
      const marginCode = getVarCodeByRole(vars, 'MARGIN');

      for (const li of s.lineItems || []) {
        const calc = li.calculations || {};
        const ci = li.costInputs || {};
        const qty = Number(li.quantity) || 1;

        // Base cost por ítem (resuelto por rol cuando existe)
        let base = 0;
        if (baseCode) {
          // El modelo declara la variable autoritativa por priceRole:
          // confiar en su valor incluso si es 0 / null / undefined (sin fallback heurístico).
          const raw = calc[baseCode] ?? ci[baseCode];
          const v = Number(raw);
          const numeric = Number.isFinite(v) ? v : 0;
          // COST_INPUT/UNIT_COST = por unidad → multiplicar por cantidad
          // COST_BASE_MARGIN = total por línea (típicamente) → no multiplicar
          const role = vars.find((v2: any) => v2.code === baseCode)?.priceRole;
          base = role === 'COST_BASE_MARGIN' ? numeric : numeric * qty;
        } else {
          // Fallback heurístico por nombre solo para modelos legacy sin priceRole.
          const fromCalc = Number(calc.COSTO_TOTAL ?? calc.COST_TOTAL ?? calc.COSTO_ALMACEN);
          const fromCalcUnit = Number(calc.COSTO_UNITARIO ?? calc.UNIT_COST);
          const fromInputs = Number(ci.COSTO_ALMACEN ?? ci.COSTO_UNITARIO ?? ci.VALOR_FOB);
          base =
            (Number.isFinite(fromCalc) && fromCalc > 0 ? fromCalc : 0) ||
            (Number.isFinite(fromCalcUnit) && fromCalcUnit > 0 ? fromCalcUnit * qty : 0) ||
            (Number.isFinite(fromInputs) && fromInputs > 0 ? fromInputs * qty : 0);
        }
        totalBaseCost += base;

        // Margen monto por ítem
        let margin: number | null = null;
        if (marginCode) {
          const v = Number(calc[marginCode]);
          if (Number.isFinite(v) && v >= 0) margin = v;
        }
        if (margin === null) {
          for (const candidate of [
            calc.MONTO_MARGEN,
            calc.MARGEN_MONTO,
            calc.GANANCIA,
            calc.UTILIDAD,
          ]) {
            const n = Number(candidate);
            if (Number.isFinite(n) && n >= 0) {
              margin = n;
              break;
            }
          }
        }
        if (margin !== null) {
          anyMarginFound = true;
          totalMargin += margin;
        }
      }
    }

    if (totalBaseCost <= 0) return null;
    const pct = anyMarginFound ? (totalMargin / totalBaseCost) * 100 : null;
    return { baseCost: totalBaseCost, marginAmount: anyMarginFound ? totalMargin : null, pct };
  })();

  // Map line item id -> shipment id (for "Grupo de envío" column)
  const lineItemShipmentMap = new Map<string, string>();
  shipments.forEach((s) => {
    s.lineItems?.forEach((li) => {
      if (li.id) lineItemShipmentMap.set(li.id, s.id);
    });
  });
  const lineItemFreightMap = new Map<string, number>();
  // 1. Backend prorateo (líneas persistidas con proratedFreightCost)
  shipments.forEach((s) => {
    s.lineItems?.forEach((li) => {
      if (li.id && li.proratedFreightCost != null && li.proratedFreightCost > 0)
        lineItemFreightMap.set(li.id, li.proratedFreightCost);
    });
  });
  // 2. Fallback: prorateo local por totalPrice cuando el backend no lo guardó
  shipments.forEach((s) => {
    const totalCost = Number(s.totalCost) || 0;
    if (totalCost <= 0) return;
    const assigned = (s.lineItems ?? []).filter((li) => li.id && !lineItemFreightMap.has(li.id));
    if (assigned.length === 0) return;
    const sumPrices = assigned.reduce((sum, li) => sum + (Number(li.totalPrice) || 0), 0);
    assigned.forEach((li) => {
      const liCost = Number(li.totalPrice) || 0;
      const share = sumPrices > 0 ? totalCost * (liCost / sumPrices) : totalCost / assigned.length;
      lineItemFreightMap.set(li.id!, share);
    });
  });
  // 3. Último fallback: si quedó algún shipment con totalCost y ningún ítem asignado,
  // prorratear entre TODAS las líneas de la cotización (líneas huérfanas sin shipmentId).
  if (lineItemFreightMap.size === 0 && shipments.some((s) => (Number(s.totalCost) || 0) > 0)) {
    const orphanTotalCost = shipments.reduce((sum, s) => sum + (Number(s.totalCost) || 0), 0);
    const allItems: Array<{ id: string; totalPrice: number }> = [];
    (currentQuote?.sections ?? []).forEach((sec: any) => {
      (sec.lineItems ?? []).forEach((li: any) => {
        if (li.id) allItems.push({ id: li.id, totalPrice: Number(li.totalPrice) || 0 });
      });
    });
    if (allItems.length === 0 && currentQuote?.lineItems) {
      currentQuote.lineItems.forEach((li: any) => {
        if (li.id) allItems.push({ id: li.id, totalPrice: Number(li.totalPrice) || 0 });
      });
    }
    const sumAll = allItems.reduce((sum, li) => sum + li.totalPrice, 0);
    allItems.forEach((li) => {
      const share =
        sumAll > 0 ? orphanTotalCost * (li.totalPrice / sumAll) : orphanTotalCost / allItems.length;
      lineItemFreightMap.set(li.id, share);
    });
  }

  const handleAssignShipment = async (lineItemId: string, shipmentId: string) => {
    if (!id || !shipmentId) return;
    try {
      const updated = await shipmentsService.assignItems(id, shipmentId, [lineItemId]);
      // Refresh full list to reflect re-assignment
      const all = await shipmentsService.list(id);
      setShipments(all.map((s) => (s.id === updated.id ? updated : s)));
      toast({ title: 'Ítem asignado al grupo de envío' });
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo asignar el ítem',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (id) {
      fetchQuote(id);
      // Load linked contracts (2.7c: sólo si la empresa tiene Contratos; el servidor daría 403)
      if (empresaTieneAlgunModulo(['menu:contracts'])) {
        contractService
          .list({ quoteId: id })
          .then((r) => setQuoteContracts(r.data))
          .catch(() => {});
      }
    }
    fetchTags();
  }, [id]);

  const handleGenerateContract = async () => {
    if (!id) return;
    try {
      setGeneratingContract(true);
      const contract = await contractService.generateFromQuote(id);
      toast({ title: 'Contrato generado exitosamente' });
      navigate(`/contracts/${contract.id}`);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo generar el contrato',
        variant: 'destructive',
      });
    } finally {
      setGeneratingContract(false);
    }
  };

  const handleEdit = () => {
    navigate(`/quotes/${id}/edit`);
  };

  const handleDelete = async () => {
    if (
      !(await confirmar({
        title: '¿Eliminar esta cotización?',
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    ) {
      return;
    }

    try {
      await deleteQuote(id!);
      toast({
        title: 'Cotización eliminada',
        variant: 'success',
      });
      navigate('/quotes');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setIsGeneratingPdf(true);
      // Una cotización del Pricer (espejo) ya no sale por `/api/pdf`: su documento es `documento.pdf`.
      if (esCodigoDelPricer(currentQuote?.code)) {
        const delEspejo = await descargarDocumentoDelEspejo(id!, currentQuote!.code);
        if (delEspejo) {
          guardarArchivo(delEspejo);
          toast({ title: 'PDF descargado', variant: 'success' });
          return;
        }
        // Prefijo `PD` pero sin ficha del Pricer vinculada: es una cotización normal, sigue por `/api/pdf`.
      }
      const { default: axiosInstance } = await import('@/lib/api/axios-instance');
      const response = await axiosInstance.post(
        `/api/pdf/quotes/${id}`,
        {},
        { responseType: 'arraybuffer' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName = `${currentQuote?.code || 'cotización'}.pdf`;

      // Use File API + navigator for reliable download on HTTP
      if ((navigator as any).msSaveOrOpenBlob) {
        (navigator as any).msSaveOrOpenBlob(blob, fileName);
      } else {
        guardarArchivo({ blob, nombre: fileName });
      }
      toast({ title: 'PDF descargado', variant: 'success' });
    } catch (error: any) {
      const descripcion = await mensajeDelServidor(
        error,
        error?.message ?? 'No se pudo generar el PDF'
      );
      toast({ title: 'Error al generar PDF', description: descripcion, variant: 'destructive' });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleResubmit = async () => {
    try {
      const { apiClient } = await import('@/lib/api');
      await apiClient.post(`/api/quotes/${id}/resubmit`, {});
      toast({ title: 'Cotización reenviada para aprobación', variant: 'success' });
      fetchQuote(id!);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getStatusBadge = (status: QuoteStatus) => {
    const variants: Record<QuoteStatus, { variant: any; label: string }> = {
      [QuoteStatus.DRAFT]: { variant: 'default', label: 'Borrador' },
      [QuoteStatus.PENDING_APPROVAL]: { variant: 'warning', label: 'Pendiente Aprobación' },
      [QuoteStatus.APPROVED]: { variant: 'success', label: 'Aprobada' },
      [QuoteStatus.REJECTED]: { variant: 'destructive', label: 'Rechazada' },
      [QuoteStatus.EXPIRED]: { variant: 'secondary', label: 'Expirada' },
      [QuoteStatus.FROZEN]: { variant: 'info', label: 'Congelada' },
      [QuoteStatus.ADJUSTMENT_REQUESTED]: { variant: 'warning', label: 'Ajuste Solicitado' },
      [QuoteStatus.CANCELLED]: { variant: 'outline', label: 'Cancelada' },
    };

    const config = variants[status] || variants[QuoteStatus.DRAFT];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Check if quote is expiring soon (within 7 days)
  const isExpiringSoon = currentQuote?.validUntil
    ? new Date(currentQuote.validUntil).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000 &&
      new Date(currentQuote.validUntil).getTime() > Date.now()
    : false;

  // Get last adjustment request approval
  const lastAdjustment = currentQuote?.approvals?.find(
    (a: any) => a.status === 'ADJUSTMENT_REQUESTED'
  );

  if (isLoading) {
    return (
      <AppLayout>
        <div className="container mx-auto p-8">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Cargando cotización...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!currentQuote) {
    return (
      <AppLayout>
        <div className="container mx-auto p-8">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Cotización no encontrada</p>
            <Button onClick={() => navigate('/quotes')} variant="outline" className="mt-4">
              Volver a Cotizaciones
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="container mx-auto p-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/quotes')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <div className="mb-3">
                    <EtiquetaDePagina modulo="cotiza">Cotiza · Gestión</EtiquetaDePagina>
                  </div>
                  <h1 className={CLASE_TITULO_DE_PAGINA}>{currentQuote.code}</h1>
                  {currentQuote.quoteType && (
                    <Badge variant="outline" className="text-sm">
                      {currentQuote.quoteType.name}
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground mt-2">
                  Cotización para {currentQuote.clientName}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Download PDF */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
              >
                <Download className="w-4 h-4 mr-2" />
                {isGeneratingPdf ? 'Generando...' : 'Descargar PDF'}
              </Button>

              {/* Save as Template */}
              <Popover
                open={showTemplatePopover}
                onOpenChange={(open) => {
                  setShowTemplatePopover(open);
                  if (!open) setTemplateNameInput('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <BookmarkPlus className="w-4 h-4 mr-2" />
                    Guardar como Plantilla
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64" align="end">
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Nombre de la Plantilla</p>
                    <Input
                      placeholder="Ej: Cotización tipo YPFB..."
                      value={templateNameInput}
                      onChange={(e) => setTemplateNameInput(e.target.value)}
                      className="h-8 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && templateNameInput.trim()) {
                          saveAsTemplate(id!, templateNameInput.trim()).then(() => {
                            toast({ title: 'Plantilla guardada', variant: 'success' });
                            setShowTemplatePopover(false);
                            setTemplateNameInput('');
                          });
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        saveAsTemplate(id!, templateNameInput.trim()).then(() => {
                          toast({ title: 'Plantilla guardada', variant: 'success' });
                          setShowTemplatePopover(false);
                          setTemplateNameInput('');
                        });
                      }}
                      disabled={!templateNameInput.trim()}
                    >
                      Guardar
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              {currentQuote.permisos?.puedeCompartir && (
                <Button variant="outline" onClick={() => setShowShareDialog(true)}>
                  <Link2 className="w-4 h-4 mr-2" />
                  {currentQuote.publicTrackingToken
                    ? 'Link cliente activo'
                    : 'Compartir con cliente'}
                </Button>
              )}
              {(currentQuote.status === QuoteStatus.DRAFT ||
                currentQuote.status === QuoteStatus.ADJUSTMENT_REQUESTED) && (
                <>
                  <Button onClick={handleEdit} variant="outline">
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  {currentQuote.status === QuoteStatus.ADJUSTMENT_REQUESTED && (
                    <Button onClick={handleResubmit}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reenviar
                    </Button>
                  )}
                  {currentQuote.permisos?.puedeEliminar && (
                    <Button onClick={handleDelete} variant="destructive">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="flex items-center gap-2 flex-wrap">
            <Tags className="w-4 h-4 text-muted-foreground" />
            {currentQuote.tags?.map((tag) => (
              <TagBadge
                key={tag.id}
                tag={tag}
                size="md"
                onRemove={() => removeQuoteTag(currentQuote.id, tag.id)}
              />
            ))}
            <Popover open={showTagSelector} onOpenChange={setShowTagSelector}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7">
                  <Tags className="w-3.5 h-3.5 mr-1" />
                  Etiquetar
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="start">
                <TagSelector
                  quoteId={currentQuote.id}
                  selectedTags={currentQuote.tags || []}
                  onClose={() => setShowTagSelector(false)}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Source Quote Banner (price chain upstream) */}
          {currentQuote.sourceQuote && (
            <Card className="border-info-borde bg-info-suave">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Layers className="w-5 h-5 text-primary" />
                  <p className="text-sm text-info">
                    Costos tomados de la cotización{' '}
                    {currentQuote.sourceQuote.quoteType && (
                      <Badge variant="outline" className="mx-1 text-xs">
                        {currentQuote.sourceQuote.quoteType.name}
                      </Badge>
                    )}
                    <Link
                      to={`/quotes/${currentQuote.sourceQuote.id}`}
                      className="font-semibold underline hover:text-foreground"
                    >
                      {currentQuote.sourceQuote.code}
                    </Link>{' '}
                    ({currentQuote.sourceQuote.clientName})
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cloned From Banner */}
          {currentQuote.clonedFrom && (
            <Card className="border-border bg-muted">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Copy className="w-5 h-5 text-muted-foreground" />
                  <p className="text-sm text-foreground">
                    Cotización clonada de{' '}
                    <Link
                      to={`/quotes/${currentQuote.clonedFrom.id}`}
                      className="font-semibold underline hover:text-foreground"
                    >
                      {currentQuote.clonedFrom.code}
                    </Link>{' '}
                    ({currentQuote.clonedFrom.clientName})
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Adjustment Banner */}
          {currentQuote.status === QuoteStatus.ADJUSTMENT_REQUESTED && lastAdjustment && (
            <Card className="border-alerta-borde bg-alerta-suave">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-alerta mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-alerta">Ajuste Solicitado por Aprobador</p>
                    {lastAdjustment.comments && (
                      <p className="text-sm text-alerta mt-1">{lastAdjustment.comments}</p>
                    )}
                    {lastAdjustment.suggestedPrice && (
                      <p className="text-sm font-medium text-alerta mt-1">
                        Precio sugerido: {currentQuote.currency?.symbol}{' '}
                        {formatCurrency(lastAdjustment.suggestedPrice).replace(/[^\d,.-]/g, '')}
                      </p>
                    )}
                    <p className="text-xs text-alerta mt-2">
                      Edita la cotizacion y haz clic en "Reenviar" para someter a aprobacion
                      nuevamente.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Approval Flow Timeline */}
          {currentQuote.approvals && currentQuote.approvals.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5" />
                  Flujo de Aprobación
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative pl-6">
                  {/* Vertical line */}
                  <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-border" />
                  <div className="space-y-4">
                    {currentQuote.approvals
                      .sort(
                        (a: any, b: any) =>
                          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                      )
                      .map((approval: any) => {
                        const statusConfig: Record<
                          string,
                          { color: string; bg: string; label: string }
                        > = {
                          PENDING: {
                            color: 'bg-alerta',
                            bg: 'bg-alerta-suave border-alerta-borde',
                            label: 'Pendiente',
                          },
                          APPROVED: {
                            color: 'bg-ok',
                            bg: 'bg-ok-suave border-ok-borde',
                            label: 'Aprobada',
                          },
                          REJECTED: {
                            color: 'bg-bloqueo',
                            bg: 'bg-bloqueo-suave border-bloqueo-borde',
                            label: 'Rechazada',
                          },
                          ADJUSTMENT_REQUESTED: {
                            color: 'bg-alerta',
                            bg: 'bg-alerta-suave border-alerta-borde',
                            label: 'Ajuste Solicitado',
                          },
                        };
                        const config = statusConfig[approval.status] || statusConfig.PENDING;
                        return (
                          <div key={approval.id} className="relative">
                            {/* Dot */}
                            <div
                              className={`absolute -left-6 top-2.5 w-3 h-3 rounded-full ${config.color} ring-2 ring-white`}
                            />
                            <div className={`border rounded-lg p-3 ${config.bg}`}>
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant={
                                      approval.status === 'APPROVED'
                                        ? 'success'
                                        : approval.status === 'REJECTED'
                                          ? 'destructive'
                                          : 'warning'
                                    }
                                  >
                                    {config.label}
                                  </Badge>
                                  {approval.requestedBy && (
                                    <span className="text-sm text-muted-foreground">
                                      Solicitada por {approval.requestedBy.firstName}{' '}
                                      {approval.requestedBy.lastName}
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {formatDateString(approval.createdAt)}
                                </span>
                              </div>
                              {approval.approvedBy && (
                                <p className="text-sm mt-1">
                                  <span className="text-muted-foreground">
                                    {approval.status === 'APPROVED'
                                      ? 'Aprobada'
                                      : approval.status === 'REJECTED'
                                        ? 'Rechazada'
                                        : 'Procesada'}
                                  </span>{' '}
                                  por{' '}
                                  <span className="font-medium">
                                    {approval.approvedBy.firstName} {approval.approvedBy.lastName}
                                  </span>
                                  {approval.resolvedAt && (
                                    <span className="text-xs text-muted-foreground ml-2">
                                      el {formatDateString(approval.resolvedAt)}
                                    </span>
                                  )}
                                </p>
                              )}
                              {approval.comments && (
                                <p className="text-sm mt-1 italic text-muted-foreground">
                                  "{approval.comments}"
                                </p>
                              )}
                              {approval.suggestedPrice && (
                                <p className="text-sm mt-1 font-medium">
                                  Precio sugerido: {currentQuote.currency?.symbol}{' '}
                                  {formatCurrency(approval.suggestedPrice).replace(/[^\d,.-]/g, '')}
                                </p>
                              )}
                              {/* Votos individuales */}
                              {approval.votes && approval.votes.length > 0 && (
                                <div className="mt-3 pt-2 border-t border-dashed space-y-1.5">
                                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Votos ({approval.votes.length})
                                  </p>
                                  {approval.votes.map((vote: any) => (
                                    <div
                                      key={vote.id}
                                      className="flex items-center justify-between text-sm"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span
                                          className={`w-2 h-2 rounded-full ${
                                            vote.action === 'APPROVE'
                                              ? 'bg-ok'
                                              : vote.action === 'REJECT'
                                                ? 'bg-bloqueo'
                                                : 'bg-alerta'
                                          }`}
                                        />
                                        <span className="font-medium">
                                          {vote.user?.firstName} {vote.user?.lastName}
                                        </span>
                                        <Badge variant="outline" className="text-[10px] py-0">
                                          {vote.action === 'APPROVE'
                                            ? 'Aprobó'
                                            : vote.action === 'REJECT'
                                              ? 'Rechazó'
                                              : 'Ajuste'}
                                        </Badge>
                                      </div>
                                      <span className="text-xs text-muted-foreground">
                                        {vote.votedAt ? formatDateString(vote.votedAt) : ''}
                                      </span>
                                    </div>
                                  ))}
                                  {approval.votes.some((v: any) => v.comments) && (
                                    <div className="space-y-1 mt-1">
                                      {approval.votes
                                        .filter((v: any) => v.comments)
                                        .map((v: any) => (
                                          <p
                                            key={v.id}
                                            className="text-xs italic text-muted-foreground pl-4"
                                          >
                                            {v.user?.firstName}: "{v.comments}"
                                          </p>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expiration Warning */}
          {currentQuote.status === QuoteStatus.EXPIRED && (
            <Card className="border-bloqueo-borde bg-bloqueo-suave">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-bloqueo" />
                  <p className="font-semibold text-bloqueo">
                    Esta cotizacion ha expirado ({formatDateString(currentQuote.validUntil!)})
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {isExpiringSoon && currentQuote.status !== QuoteStatus.EXPIRED && (
            <Card className="border-alerta-borde bg-alerta-suave">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-alerta" />
                  <p className="font-semibold text-alerta">
                    Esta cotizacion expira pronto ({formatDateString(currentQuote.validUntil!)})
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Información General */}
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Información del Cliente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Cliente</p>
                  <p className="font-semibold">{currentQuote.clientName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-semibold">{currentQuote.clientEmail}</p>
                </div>
                {currentQuote.clientPhone && (
                  <div>
                    <p className="text-sm text-muted-foreground">Teléfono</p>
                    <p className="font-semibold">{currentQuote.clientPhone}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Detalles de la Cotización</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <div className="mt-1">{getStatusBadge(currentQuote.status)}</div>
                </div>
                {currentQuote.country && (
                  <div>
                    <p className="text-sm text-muted-foreground">País</p>
                    <p className="font-semibold">
                      {currentQuote.country.code} - {currentQuote.country.name}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Modelo de Pricing</p>
                  <p className="font-semibold">
                    {currentQuote.sections && currentQuote.sections.length > 0
                      ? `${currentQuote.sections.length} sección(es)`
                      : currentQuote.model?.name || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tiempo de Validez</p>
                  <p className="font-semibold">{currentQuote.validityDays} días</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Válida hasta</p>
                  <p className="font-semibold">{formatDateString(currentQuote.validUntil!)}</p>
                </div>
                {currentQuote.c4cOpportunityId && (
                  <div>
                    <p className="text-sm text-muted-foreground">OPP C4C ID</p>
                    <p className="font-semibold">{currentQuote.c4cOpportunityId}</p>
                  </div>
                )}
                {currentQuote.c4cTaskId && (
                  <div>
                    <p className="text-sm text-muted-foreground">TAREA C4C ID</p>
                    <p className="font-semibold">{currentQuote.c4cTaskId}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Creada</p>
                  <p className="font-semibold">{formatDateString(currentQuote.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Creado por</p>
                  <p className="font-semibold">
                    {currentQuote.createdBy
                      ? `${currentQuote.createdBy.firstName} ${currentQuote.createdBy.lastName}`
                      : 'N/A'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Resumen Financiero */}
          <Card>
            <CardHeader>
              <CardTitle>Resumen Financiero</CardTitle>
              <CardDescription>Desglose de costos y márgenes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <CurrencySimulator
                  baseCurrencyCode={currentQuote.currency?.code || 'USD'}
                  baseCurrencySymbol={currentQuote.currency?.symbol || '$'}
                  referenceDate={
                    currentQuote.createdAt ? String(currentQuote.createdAt).slice(0, 10) : undefined
                  }
                  amounts={[
                    // FP-4 (M-1): sin «Ver costos y márgenes» el espejo llega sin costo base.
                    ...(currentQuote.costosOcultos
                      ? []
                      : [
                          {
                            label: 'Costo Base',
                            value: liveMargin?.baseCost ?? currentQuote.baseCost,
                          },
                        ]),
                    {
                      label: 'Precio Sugerido',
                      value: currentQuote.suggestedPrice,
                    },
                    {
                      label: 'Precio Final',
                      value: currentQuote.finalPrice,
                      emphasis: true,
                    },
                    ...(liveMargin?.marginAmount != null
                      ? [{ label: 'Margen (monto)', value: liveMargin.marginAmount }]
                      : []),
                  ]}
                />
                {!currentQuote.costosOcultos && (
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="text-muted-foreground">
                      Costo Base
                      {liveMargin && (
                        <span className="text-[10px] text-muted-foreground ml-1">
                          (Costo Base Margen)
                        </span>
                      )}
                    </span>
                    <span className="font-semibold">
                      {currentQuote.currency?.symbol}{' '}
                      {formatCurrency(liveMargin?.baseCost ?? currentQuote.baseCost).replace(
                        /[^\d,.-]/g,
                        ''
                      )}
                    </span>
                  </div>
                )}
                {currentQuote.costosOcultos && (
                  <p className="text-xs text-muted-foreground" data-testid="costos-ocultos">
                    Tu cargo no ve costos ni márgenes: se muestran sólo los precios.
                  </p>
                )}
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="text-muted-foreground">Precio Sugerido</span>
                  <span className="font-semibold">
                    {currentQuote.currency?.symbol}{' '}
                    {formatCurrency(currentQuote.suggestedPrice).replace(/[^\d,.-]/g, '')}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="text-muted-foreground">Precio Final</span>
                  <span className="text-2xl font-bold text-primary">
                    {currentQuote.currency?.symbol}{' '}
                    {formatCurrency(currentQuote.finalPrice).replace(/[^\d,.-]/g, '')}
                  </span>
                </div>
                {liveMargin?.marginAmount != null && (
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="text-muted-foreground">Margen (monto)</span>
                    <span className="font-semibold text-ok">
                      {currentQuote.currency?.symbol}{' '}
                      {formatCurrency(liveMargin.marginAmount).replace(/[^\d,.-]/g, '')}
                    </span>
                  </div>
                )}
                {/* Margen Real solo se muestra si:
                    - El modelo declara MARGIN (via rol o nombre conocido) → usamos liveMargin.pct
                    - O la cotización tiene un actualMargin guardado > 0 (modelos viejos)
                    Para cotizaciones de importación puras (solo costos), se oculta porque no aplica.
                    Nota: actualMargin puede venir como ratio (0.25) o como porcentaje (25) según
                    cuándo fue creada la cotización. Si es < 1 lo tratamos como ratio y lo escalamos. */}
                {(() => {
                  const rawActual = currentQuote.actualMargin;
                  const normalizedActualMargin =
                    rawActual != null && rawActual > 0 && rawActual < 1
                      ? rawActual * 100
                      : rawActual;
                  const marginPct = liveMargin?.pct ?? normalizedActualMargin;
                  if (currentQuote.costosOcultos) return null;
                  if (liveMargin?.pct == null && !(currentQuote.actualMargin > 0)) return null;
                  return (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">
                        Margen Real
                        {liveMargin?.pct != null && (
                          <span className="text-[10px] text-muted-foreground ml-1">
                            (Margen / Costo Base Margen)
                          </span>
                        )}
                      </span>
                      <span
                        className={`text-xl font-bold ${
                          marginPct >= currentQuote.targetMargin
                            ? 'text-ok'
                            : marginPct >= (currentQuote.model?.floorMarginPercentage || 0)
                              ? 'text-alerta'
                              : 'text-destructive'
                        }`}
                      >
                        {marginPct.toFixed(2)}%
                      </span>
                    </div>
                  );
                })()}
                {currentQuote.adjustmentJustification && (
                  <div className="pt-2 border-t">
                    <p className="text-sm text-muted-foreground mb-1">Justificación de Ajuste</p>
                    <p className="text-sm">{currentQuote.adjustmentJustification}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Multi-Pricer Sections */}
          {currentQuote.sections && currentQuote.sections.length > 0 && (
            <div className="space-y-4">
              {currentQuote.sections
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((section, sIdx) => {
                  // Get variables from the model to show full pricing breakdown
                  const modelVars: Variable[] = section.model?.variables ?? [];
                  // Merge per-item + global input variables, sorted by order (Excel-like)
                  const allColumns = [
                    ...modelVars.filter((v) => !v.isGlobal && v.code !== 'CANTIDAD'),
                    ...modelVars.filter((v) => v.isGlobal && !v.formula),
                  ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                  const sectionTotal =
                    section.lineItems?.reduce((sum, li) => sum + li.totalPrice, 0) ?? 0;

                  return (
                    <Card key={section.id} className="border-2 border-dashed">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Layers className="w-5 h-5" />
                          Seccion {sIdx + 1} — {section.model?.name || 'Modelo'}
                          {sectionTotal > 0 && (
                            <span className="ml-auto text-sm font-semibold bg-alerta-suave text-foreground px-3 py-1 rounded-full">
                              Subtotal: {currentQuote.currency?.symbol} {formatNumber(sectionTotal)}
                            </span>
                          )}
                        </CardTitle>
                        <CardDescription>
                          {section.model?.code && `Código: ${section.model.code}`}
                          {modelVars.length > 0 && ` — ${modelVars.length} variables`}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Line Items with full variable breakdown */}
                        {section.lineItems && section.lineItems.length > 0 ? (
                          <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left p-2 font-medium w-8 bg-muted/50">#</th>
                                  <th className="text-left p-2 font-medium min-w-[160px] bg-muted/50">
                                    Material
                                  </th>
                                  <th className="text-right p-2 font-medium w-16 bg-info-suave text-info">
                                    Cant.
                                  </th>
                                  {allColumns.map((v) => {
                                    const isGlobal = !!v.isGlobal;
                                    const isCalc =
                                      !!v.formula || v.variableType === VariableType.CALCULATED;
                                    const isTiered = v.variableType === VariableType.TIERED;
                                    const isEnum = v.variableType === VariableType.ENUM;
                                    const isLookup = v.variableType === VariableType.LOOKUP;
                                    return (
                                      <Tooltip key={v.code}>
                                        <TooltipTrigger asChild>
                                          <th
                                            className={`text-right p-2 font-medium min-w-[100px] text-xs cursor-help ${
                                              isGlobal
                                                ? 'bg-muted text-muted-foreground'
                                                : isTiered
                                                  ? 'bg-purple-50 text-purple-800'
                                                  : isCalc
                                                    ? 'bg-ok-suave text-ok'
                                                    : isEnum || isLookup
                                                      ? 'bg-info-suave text-info'
                                                      : 'bg-info-suave text-info'
                                            }`}
                                          >
                                            {v.name}
                                          </th>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="font-mono text-xs">{v.code}</p>
                                          {isGlobal && (
                                            <p className="text-xs text-muted-foreground">
                                              Global (compartido)
                                            </p>
                                          )}
                                          {v.formula && (
                                            <p className="text-xs">Formula: {v.formula}</p>
                                          )}
                                          {v.description && (
                                            <p className="text-xs">{v.description}</p>
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    );
                                  })}
                                  <th className="text-right p-2 font-medium w-24 bg-alerta-suave text-foreground border-l-2 border-alerta-borde">
                                    Precio Unit.
                                  </th>
                                  <th className="text-right p-2 font-medium w-24 bg-alerta-suave text-foreground">
                                    Total
                                  </th>
                                  {isImportQuote && lineItemFreightMap.size > 0 && (
                                    <th className="text-right p-2 font-medium w-24 bg-info-suave text-foreground border-l-2 border-info-borde">
                                      Flete
                                    </th>
                                  )}
                                  {isImportQuote && (
                                    <th className="text-right p-2 font-medium w-24 bg-alerta-suave text-foreground border-l-2 border-alerta-borde">
                                      GA
                                    </th>
                                  )}
                                  {isImportQuote && lineItemFreightMap.size > 0 && (
                                    <th className="text-right p-2 font-medium w-28 bg-ok-suave text-foreground border-l-2 border-ok-borde">
                                      C.Alm. Unit.
                                    </th>
                                  )}
                                  {isImportQuote && lineItemFreightMap.size > 0 && (
                                    <th className="text-right p-2 font-medium w-32 bg-ok-suave text-foreground">
                                      Costo Almacén
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {section.lineItems.map((item, idx) => (
                                  <tr key={item.id || idx} className="border-b hover:bg-muted/20">
                                    <td className="p-2 text-muted-foreground">{idx + 1}</td>
                                    <td className="p-2 font-medium">
                                      {item.material
                                        ? `${item.material.code} - ${item.material.name}`
                                        : item.materialId}
                                    </td>
                                    <td className="p-2 text-right font-mono bg-info-suave">
                                      {item.quantity}
                                    </td>
                                    {(() => {
                                      // Compute live values for this item (fallback when calculations not stored)
                                      const liveResults = calculateVariables(
                                        modelVars,
                                        {
                                          ...(section.globalInputs as Record<string, any>),
                                          ...item.costInputs,
                                          CANTIDAD: item.quantity,
                                        },
                                        section.model?.lookupTables ?? []
                                      );
                                      const liveMap: Record<string, any> = {};
                                      liveResults.forEach((r) => {
                                        liveMap[r.variableCode] = r.value;
                                      });

                                      return allColumns.map((v) => {
                                        const isGlobal = !!v.isGlobal;
                                        const isCalc =
                                          !!v.formula || v.variableType === VariableType.CALCULATED;
                                        const isTiered = v.variableType === VariableType.TIERED;
                                        const isEnum = v.variableType === VariableType.ENUM;
                                        const isLookup = v.variableType === VariableType.LOOKUP;
                                        // Global: from section.globalInputs; per-item: stored or live-computed
                                        const stored = isGlobal
                                          ? (section.globalInputs as Record<string, any>)?.[v.code!]
                                          : (item.calculations?.[v.code!] ??
                                            item.costInputs?.[v.code!]);
                                        const val = stored != null ? stored : liveMap[v.code!];
                                        const numVal = val != null ? Number(val) : null;

                                        // Build tooltip description
                                        let tipSource = 'Entrada directa';
                                        if (v.formula) tipSource = v.formula;
                                        else if (isTiered)
                                          tipSource = `Escalonado (${v.tiers?.length || 0} tramos)`;
                                        else if (isLookup)
                                          tipSource = `Tabla: ${v.lookupTable?.name || 'lookup'}`;
                                        else if (isEnum) {
                                          const opt = v.options?.find((o) => o.value === numVal);
                                          tipSource = opt ? `Enum: ${opt.label}` : 'Enum';
                                        } else if (isGlobal)
                                          tipSource = 'Global (compartido en sección)';

                                        return (
                                          <Tooltip key={v.code}>
                                            <TooltipTrigger asChild>
                                              <td
                                                className={`p-2 text-right font-mono text-xs cursor-help ${
                                                  isGlobal
                                                    ? 'bg-muted text-muted-foreground'
                                                    : isTiered
                                                      ? 'bg-purple-50/30 text-purple-900'
                                                      : isCalc
                                                        ? 'bg-ok-suave text-foreground'
                                                        : 'bg-info-suave'
                                                }`}
                                              >
                                                {numVal != null && !isNaN(numVal)
                                                  ? formatNumber(numVal)
                                                  : '-'}
                                              </td>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="top"
                                              className="max-w-xs text-left"
                                            >
                                              <p className="font-mono text-xs font-bold">
                                                {v.code}
                                              </p>
                                              <p className="text-xs text-muted-foreground">
                                                {v.name}
                                              </p>
                                              <p className="text-xs mt-1">
                                                {v.formula ? 'Formula' : 'Fuente'}:{' '}
                                                <span className="font-mono">{tipSource}</span>
                                              </p>
                                              {numVal != null && !isNaN(numVal) && (
                                                <p className="text-xs mt-1">
                                                  ={' '}
                                                  <span className="font-mono font-bold">
                                                    {formatNumber(numVal)}
                                                  </span>
                                                </p>
                                              )}
                                            </TooltipContent>
                                          </Tooltip>
                                        );
                                      });
                                    })()}
                                    {(() => {
                                      const displayUnitPrice = resolveDisplayUnitPrice(item);
                                      const displayTotalPrice =
                                        Number.isFinite(Number(item.totalPrice)) &&
                                        Number(item.totalPrice) > 0
                                          ? Number(item.totalPrice)
                                          : displayUnitPrice * (Number(item.quantity) || 0);
                                      return (
                                        <>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <td className="p-2 text-right font-mono text-xs font-semibold bg-alerta-suave border-l-2 border-alerta-borde cursor-help">
                                                {currentQuote.currency?.symbol}{' '}
                                                {formatNumber(displayUnitPrice)}
                                              </td>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="text-left">
                                              <p className="text-xs font-bold">Precio Unitario</p>
                                              <p className="text-xs mt-1">
                                                Variable resultado del modelo de pricing
                                              </p>
                                              <p className="text-xs mt-1">
                                                ={' '}
                                                <span className="font-mono font-bold">
                                                  {currentQuote.currency?.symbol}{' '}
                                                  {formatNumber(displayUnitPrice)}
                                                </span>
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <td className="p-2 text-right font-mono text-xs font-bold bg-alerta-suave cursor-help">
                                                {currentQuote.currency?.symbol}{' '}
                                                {formatNumber(displayTotalPrice)}
                                              </td>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="text-left">
                                              <p className="text-xs font-bold">Total Línea</p>
                                              <p className="text-xs font-mono mt-1">
                                                {formatNumber(item.quantity, 0)} ×{' '}
                                                {currentQuote.currency?.symbol}{' '}
                                                {formatNumber(displayUnitPrice)}
                                              </p>
                                              <p className="text-xs mt-1">
                                                ={' '}
                                                <span className="font-mono font-bold">
                                                  {currentQuote.currency?.symbol}{' '}
                                                  {formatNumber(displayTotalPrice)}
                                                </span>
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </>
                                      );
                                    })()}
                                    {/* Flete */}
                                    {isImportQuote && lineItemFreightMap.size > 0 && (
                                      <td className="p-2 text-right font-mono text-xs font-semibold bg-info-suave border-l-2 border-info-borde text-foreground">
                                        {currentQuote.currency?.symbol}{' '}
                                        {formatNumber(lineItemFreightMap.get(item.id!) ?? 0)}
                                      </td>
                                    )}
                                    {/* GA */}
                                    {isImportQuote &&
                                      (() => {
                                        const gaRate =
                                          Number(item.costInputs?.GA_PCT) ||
                                          (item.material as any)?.activeTariffRate ||
                                          0;
                                        const freight = lineItemFreightMap.get(item.id!) ?? 0;
                                        const gaAmount =
                                          gaRate > 0
                                            ? ((item.totalPrice + freight) * gaRate) / 100
                                            : 0;
                                        return (
                                          <td
                                            className="p-2 text-right font-mono text-xs font-semibold bg-alerta-suave border-l-2 border-alerta-borde text-foreground"
                                            title={`GA ${gaRate}% sobre (costo + flete)`}
                                          >
                                            <div>{gaRate}%</div>
                                            <div>
                                              {currentQuote.currency?.symbol}{' '}
                                              {formatNumber(gaAmount)}
                                            </div>
                                          </td>
                                        );
                                      })()}
                                    {/* Costo Almacén Unit. + Total */}
                                    {isImportQuote &&
                                      lineItemFreightMap.size > 0 &&
                                      (() => {
                                        const freight = lineItemFreightMap.get(item.id!) ?? 0;
                                        const gaRate =
                                          Number(item.costInputs?.GA_PCT) ||
                                          (item.material as any)?.activeTariffRate ||
                                          0;
                                        const gaAmount =
                                          gaRate > 0
                                            ? ((item.totalPrice + freight) * gaRate) / 100
                                            : 0;
                                        const costoAlmacen = item.totalPrice + freight + gaAmount;
                                        const costoAlmacenUnit =
                                          item.quantity > 0 ? costoAlmacen / item.quantity : 0;
                                        return (
                                          <>
                                            <td className="p-2 text-right font-mono text-xs font-semibold bg-ok-suave border-l-2 border-ok-borde text-foreground">
                                              {currentQuote.currency?.symbol}{' '}
                                              {formatNumber(costoAlmacenUnit)}
                                            </td>
                                            <td className="p-2 text-right font-mono text-xs font-extrabold bg-ok-suave text-foreground">
                                              {currentQuote.currency?.symbol}{' '}
                                              {formatNumber(costoAlmacen)}
                                            </td>
                                          </>
                                        );
                                      })()}
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="border-t-2 bg-muted/50 font-bold">
                                  <td
                                    colSpan={3 + allColumns.length + 1}
                                    className="p-2 text-right"
                                  >
                                    SUBTOTAL SECCION:
                                  </td>
                                  <td className="p-2 text-right font-mono bg-alerta-suave">
                                    {currentQuote.currency?.symbol} {formatNumber(sectionTotal)}
                                  </td>
                                  {isImportQuote && lineItemFreightMap.size > 0 && (
                                    <td className="p-2 text-right font-mono bg-info-suave text-foreground">
                                      {currentQuote.currency?.symbol}{' '}
                                      {formatNumber(
                                        (section.lineItems ?? []).reduce(
                                          (sum, li) => sum + (lineItemFreightMap.get(li.id!) ?? 0),
                                          0
                                        )
                                      )}
                                    </td>
                                  )}
                                  {isImportQuote && (
                                    <td className="p-2 text-right font-mono bg-alerta-suave text-foreground">
                                      {currentQuote.currency?.symbol}{' '}
                                      {formatNumber(
                                        (section.lineItems ?? []).reduce((sum, li) => {
                                          const r =
                                            Number(li.costInputs?.GA_PCT) ||
                                            (li.material as any)?.activeTariffRate ||
                                            0;
                                          const freight = lineItemFreightMap.get(li.id!) ?? 0;
                                          return (
                                            sum +
                                            (r > 0 ? ((li.totalPrice + freight) * r) / 100 : 0)
                                          );
                                        }, 0)
                                      )}
                                    </td>
                                  )}
                                  {isImportQuote &&
                                    lineItemFreightMap.size > 0 &&
                                    (() => {
                                      const items = section.lineItems ?? [];
                                      const totalAlmacen = items.reduce((sum, li) => {
                                        const freight = lineItemFreightMap.get(li.id!) ?? 0;
                                        const r =
                                          Number(li.costInputs?.GA_PCT) ||
                                          (li.material as any)?.activeTariffRate ||
                                          0;
                                        const ga =
                                          r > 0 ? ((li.totalPrice + freight) * r) / 100 : 0;
                                        return sum + li.totalPrice + freight + ga;
                                      }, 0);
                                      const totalQty = items.reduce(
                                        (sum, li) => sum + li.quantity,
                                        0
                                      );
                                      return (
                                        <>
                                          <td className="p-2 text-right font-mono bg-ok-suave text-foreground">
                                            {currentQuote.currency?.symbol}{' '}
                                            {formatNumber(
                                              totalQty > 0 ? totalAlmacen / totalQty : 0
                                            )}
                                          </td>
                                          <td className="p-2 text-right font-mono font-extrabold bg-ok-suave text-foreground">
                                            {currentQuote.currency?.symbol}{' '}
                                            {formatNumber(totalAlmacen)}
                                          </td>
                                        </>
                                      );
                                    })()}
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">Sin items en esta sección</p>
                        )}

                        {/* Color legend */}
                        {allColumns.length > 0 && (
                          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                            <span className="flex items-center gap-1">
                              <span className="inline-block w-3 h-3 rounded bg-info-suave border border-info-borde" />
                              Ingresado
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="inline-block w-3 h-3 rounded bg-muted border border-border" />
                              Global (sección)
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="inline-block w-3 h-3 rounded bg-ok-suave border border-ok-borde" />
                              Calculado
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="inline-block w-3 h-3 rounded bg-purple-100 border border-purple-300" />
                              Escalonado
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="inline-block w-3 h-3 rounded bg-info-suave border border-info-borde" />
                              Enum / Lookup
                            </span>
                          </div>
                        )}

                        {/* Formula reference */}
                        {modelVars.filter(
                          (v) =>
                            v.formula ||
                            v.variableType === VariableType.TIERED ||
                            v.variableType === VariableType.LOOKUP
                        ).length > 0 &&
                          (() => {
                            const sectionKey = section.id ?? `section-${sIdx}`;
                            const isFormulasExpanded = expandedFormulaSections.has(sectionKey);
                            const toggleFormulas = () => {
                              setExpandedFormulaSections((prev) => {
                                const next = new Set(prev);
                                if (next.has(sectionKey)) {
                                  next.delete(sectionKey);
                                } else {
                                  next.add(sectionKey);
                                }
                                return next;
                              });
                            };
                            return (
                              <div className="bg-muted/30 rounded-lg p-3 space-y-1 mt-1">
                                <button
                                  type="button"
                                  onClick={toggleFormulas}
                                  aria-expanded={isFormulasExpanded}
                                  className="flex items-center gap-1 w-full text-left cursor-pointer select-none"
                                >
                                  {isFormulasExpanded ? (
                                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                                  )}
                                  <p className="text-xs font-semibold text-muted-foreground uppercase">
                                    Formulas del modelo
                                  </p>
                                </button>
                                {isFormulasExpanded &&
                                  modelVars
                                    .filter(
                                      (v) =>
                                        v.formula ||
                                        v.variableType === VariableType.TIERED ||
                                        v.variableType === VariableType.LOOKUP
                                    )
                                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                    .map((v) => (
                                      <div
                                        key={v.code}
                                        className="flex items-baseline gap-2 text-xs"
                                      >
                                        <span
                                          className={`font-mono font-bold px-1.5 py-0.5 rounded ${
                                            v.formula
                                              ? 'bg-ok-suave text-ok'
                                              : v.variableType === VariableType.TIERED
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-info-suave text-info'
                                          }`}
                                        >
                                          {v.code}
                                        </span>
                                        <span className="text-muted-foreground">{v.name}:</span>
                                        <span className="font-mono">
                                          {v.formula
                                            ? v.formula
                                            : v.variableType === VariableType.TIERED
                                              ? `Escalonado (${v.tiers?.length || 0} tramos por cantidad)`
                                              : `Tabla: ${v.lookupTable?.name || 'lookup'}`}
                                        </span>
                                      </div>
                                    ))}
                              </div>
                            );
                          })()}
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          )}

          {currentQuote.quoteType &&
            ['CO', 'IMPORT', 'IMP', 'IMP-SW'].includes(currentQuote.quoteType.code) && (
              <QuoteLogisticsSection
                quoteId={currentQuote.id}
                incoterm={currentQuote.incoterm || null}
                sections={(currentQuote.sections as any) ?? []}
                onShipmentsChanged={setShipments}
                readOnly
              />
            )}

          {/* Legacy Line Items (quotes without sections) */}
          {(!currentQuote.sections || currentQuote.sections.length === 0) &&
            currentQuote.lineItems &&
            currentQuote.lineItems.length > 0 &&
            (() => {
              // Collect all variable codes from costInputs + calculations across items
              const allVarCodes = new Set<string>();
              currentQuote.lineItems!.forEach((item) => {
                Object.keys(item.costInputs || {}).forEach((k) => allVarCodes.add(k));
                Object.keys(item.calculations || {}).forEach((k) => {
                  if (!k.endsWith('_error')) allVarCodes.add(k);
                });
              });
              allVarCodes.delete('CANTIDAD');
              const varCodes = Array.from(allVarCodes);

              return (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Items de Cotización
                    </CardTitle>
                    <CardDescription>
                      {currentQuote.lineItems!.length} item(s) — Desglose completo de variables por
                      item
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-2 font-medium">#</th>
                            <th className="text-left p-2 font-medium min-w-[160px]">Material</th>
                            <th className="text-right p-2 font-medium w-16">Cant.</th>
                            {varCodes.map((code) => (
                              <th
                                key={code}
                                className="text-right p-2 font-medium text-xs min-w-[90px] bg-muted/30"
                              >
                                {code.replace(/_/g, ' ')}
                              </th>
                            ))}
                            <th className="text-right p-2 font-medium min-w-[140px] bg-info-suave text-foreground">
                              Grupo de envío
                            </th>
                            <th className="text-right p-2 font-medium w-28 bg-info-suave text-foreground">
                              Flete prorrateado
                            </th>
                            <th className="text-right p-2 font-medium w-24 bg-alerta-suave text-foreground border-l-2 border-alerta-borde">
                              Precio Unit.
                            </th>
                            <th className="text-right p-2 font-medium w-24 bg-alerta-suave text-foreground">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentQuote.lineItems!.map((item, idx) => (
                            <tr key={item.id || idx} className="border-b hover:bg-muted/20">
                              <td className="p-2 text-muted-foreground">{idx + 1}</td>
                              <td className="p-2 font-medium">
                                {item.material
                                  ? `${item.material.code} - ${item.material.name}`
                                  : item.materialId}
                              </td>
                              <td className="p-2 text-right font-mono">{item.quantity}</td>
                              {varCodes.map((code) => {
                                const val = item.calculations?.[code] ?? item.costInputs?.[code];
                                const numVal = val != null ? Number(val) : null;
                                return (
                                  <td key={code} className="p-2 text-right font-mono text-xs">
                                    {numVal != null && !isNaN(numVal) ? formatNumber(numVal) : '-'}
                                  </td>
                                );
                              })}
                              <td className="p-2 bg-info-suave">
                                {item.id && shipments.length > 0 ? (
                                  <Select
                                    value={lineItemShipmentMap.get(item.id) ?? ''}
                                    onValueChange={(val) => handleAssignShipment(item.id!, val)}
                                  >
                                    <SelectTrigger className="h-7 text-xs">
                                      <SelectValue placeholder="Sin grupo" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {shipments.map((s) => (
                                        <SelectItem key={s.id} value={s.id} className="text-xs">
                                          {s.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="p-2 text-right font-mono text-xs bg-info-suave text-alerta">
                                {item.id && lineItemFreightMap.has(item.id)
                                  ? `${currentQuote.currency?.symbol ?? '$'} ${formatNumber(
                                      lineItemFreightMap.get(item.id) ?? 0
                                    )}`
                                  : '-'}
                              </td>
                              {(() => {
                                const displayUnit = resolveDisplayUnitPrice(item);
                                const displayTotal =
                                  Number.isFinite(Number(item.totalPrice)) &&
                                  Number(item.totalPrice) > 0
                                    ? Number(item.totalPrice)
                                    : displayUnit * (Number(item.quantity) || 0);
                                return (
                                  <>
                                    <td className="p-2 text-right font-mono text-xs font-semibold bg-alerta-suave border-l-2 border-alerta-borde">
                                      {currentQuote.currency?.symbol} {formatNumber(displayUnit)}
                                    </td>
                                    <td className="p-2 text-right font-mono text-xs font-bold bg-alerta-suave">
                                      {currentQuote.currency?.symbol} {formatNumber(displayTotal)}
                                    </td>
                                  </>
                                );
                              })()}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 bg-muted/50 font-bold">
                            <td colSpan={3 + varCodes.length + 3} className="p-2 text-right">
                              TOTAL:
                            </td>
                            <td className="p-2 text-right font-mono bg-alerta-suave">
                              {currentQuote.currency?.symbol}{' '}
                              {formatNumber(
                                currentQuote.lineItems!.reduce((sum, li) => {
                                  const u = resolveDisplayUnitPrice(li);
                                  const t =
                                    Number.isFinite(Number(li.totalPrice)) &&
                                    Number(li.totalPrice) > 0
                                      ? Number(li.totalPrice)
                                      : u * (Number(li.quantity) || 0);
                                  return sum + t;
                                }, 0)
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

          {/* Variables Globales */}
          {currentQuote.globalInputs && Object.keys(currentQuote.globalInputs).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Variables Globales
                </CardTitle>
                <CardDescription>
                  Valores compartidos entre todos los items de la cotización
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(currentQuote.globalInputs as Record<string, unknown>)
                    .filter(([, value]) => value === null || typeof value !== 'object')
                    .map(([key, value]) => (
                      <div
                        key={key}
                        className="flex justify-between items-center p-3 bg-muted/50 rounded-lg border"
                      >
                        <span className="text-sm font-medium text-muted-foreground">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className="font-mono font-semibold">
                          {typeof value === 'number'
                            ? formatNumber(value)
                            : value == null
                              ? '—'
                              : String(value)}
                        </span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Inputs de Costos (legacy single-item) */}
          {currentQuote.costInputs &&
            (!currentQuote.lineItems || currentQuote.lineItems.length === 0) && (
              <Card>
                <CardHeader>
                  <CardTitle>Variables de Costo</CardTitle>
                  <CardDescription>Valores ingresados para el cálculo</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-2">
                    {Object.entries(currentQuote.costInputs as Record<string, unknown>)
                      .filter(([, value]) => value === null || typeof value !== 'object')
                      .map(([key, value]) => (
                        <div
                          key={key}
                          className="flex justify-between items-center p-3 bg-muted rounded-lg"
                        >
                          <span className="text-sm font-medium">{key.replace(/_/g, ' ')}</span>
                          <span className="font-mono font-semibold">
                            {value == null ? '—' : String(value)}
                          </span>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}

          {/* Escenarios de Pago */}
          {currentQuote.paymentScenarios && currentQuote.paymentScenarios.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Escenarios de Pago</CardTitle>
                <CardDescription>Opciones de pago disponibles</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {currentQuote.paymentScenarios.map((scenario) => (
                    <div
                      key={scenario.id}
                      className="flex justify-between items-center p-4 border rounded-lg"
                    >
                      <div>
                        <p className="font-semibold">{scenario.paymentType}</p>
                        {scenario.description && (
                          <p className="text-sm text-muted-foreground">{scenario.description}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">
                          {currentQuote.currency?.symbol}{' '}
                          {formatCurrency(scenario.finalAmount).replace(/[^\d,.-]/g, '')}
                        </p>
                        {scenario.discountPercent !== 0 && (
                          <p className="text-sm text-ok">{scenario.discountPercent}% descuento</p>
                        )}
                        {scenario.interestPercent !== 0 && (
                          <p className="text-sm text-alerta">{scenario.interestPercent}% recargo</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {/* Documentación Contractual: de Contratos. Sin ese módulo en el techo, el servidor da 403: no se ofrece. */}
          {empresaTieneAlgunModulo(['menu:contracts']) && (
            <Card className="border-white/10">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileSignature className="h-5 w-5" />
                  Documentación Contractual
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {quoteContracts.length > 0 ? (
                  <div className="space-y-2">
                    {quoteContracts.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between p-2 rounded bg-white/5"
                      >
                        <div>
                          <Link
                            to={`/contracts/${c.id}`}
                            className="text-info hover:underline font-medium"
                          >
                            {c.name}
                          </Link>
                          <span className="text-xs text-muted-foreground ml-2">
                            {c.status === 'DRAFT'
                              ? 'Borrador'
                              : c.status === 'ACTIVE'
                                ? 'Activo'
                                : c.status}
                          </span>
                        </div>
                        <Badge variant={c.status === 'ACTIVE' ? 'default' : 'secondary'}>
                          {c.status === 'DRAFT'
                            ? 'Borrador'
                            : c.status === 'ACTIVE'
                              ? 'Activo'
                              : c.status === 'EXPIRED'
                                ? 'Vencido'
                                : 'Cancelado'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No hay contratos vinculados a esta cotización.
                  </p>
                )}
                {currentQuote.status === 'APPROVED' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateContract}
                    disabled={generatingContract}
                  >
                    {generatingContract ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileSignature className="h-4 w-4 mr-2" />
                    )}
                    Generar Contrato
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quien ve esta cotizacion, y por que */}
          <PanelDeAcceso quoteId={currentQuote.id} />

          {/* Comentarios */}
          <CommentsSection
            quoteId={currentQuote.id}
            puedeBorrarDeOtros={!!currentQuote.permisos?.puedeBorrarComentariosDeOtros}
          />
        </div>
      </div>

      <ShareClientDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        title={`Compartir cotización ${currentQuote.code}`}
        description="Generá un link público para que tu cliente vea la cotización (monto y detalle) sin necesidad de cuenta."
        currentToken={currentQuote.publicTrackingToken ?? null}
        currentExpiresAt={currentQuote.publicTrackingExpiresAt ?? null}
        onGenerate={async (days) => {
          const result = await quoteService.shareQuote(currentQuote.id, days);
          await fetchQuote(currentQuote.id);
          return { token: result.token, expiresAt: result.expiresAt };
        }}
        onRevoke={async () => {
          await quoteService.revokeQuoteShare(currentQuote.id);
          await fetchQuote(currentQuote.id);
        }}
        buildUrl={(t) => `${window.location.origin}/track/quote/${t}`}
      />
    </AppLayout>
  );
}

export default QuoteDetailPage;
